import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetAlarmsQuery, useUpdateSensorAlarmMutation, useGetAlarmLogQuery, useAcknowledgeAlarmLogMutation, alarmApi } from '../services/alarmApi';
import { useGetSensorListQuery } from '../services/sensorApi';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';
import { usePermissions } from '../hooks/usePermissions';
import { Bell, AlertTriangle, CheckCircle, X, Settings, Info } from 'lucide-react';
import { formatSensorSerial } from '../utils/sensorUtils';
import { getBatteryLowThresholdVolts } from '../utils/batteryUtils';
import { filterAlarmLogsBySensorAccess } from '../utils/alarmAccess';
import { expandSensors, isSplitRow, SensorRow } from '../utils/expandSensors';
import { SplitChannelDialog } from '../components/sensors/SplitChannelDialog';

/** Returns the threshold unit label for a sensor type. */
function unitLabel(type: string | number | undefined): string {
  // Types 5 & 6 are dual-temperature probes (IceSpy Dual) — thresholds are °C, same as other temp alarms.
  // Type 6 was incorrectly shown as %RH; map another type here only if high/low alarms use a non-°C unit.
  void type;
  return '°C';
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
    return (error as any).message;
  }
  return 'Unknown error';
}

const boolToText = (val: boolean) => (val ? 'Yes' : 'No');
const boolToInput = (val: boolean, onChange: (v: boolean) => void) => (
  <input type="checkbox" checked={val} onChange={e => onChange(e.target.checked)} className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
);

const ALARM_SETTING_HELP = {
  high: 'Triggers a high alarm when the sensor reading stays above this value for the configured Delay.',
  low: 'Triggers a low alarm when the sensor reading stays below this value for the configured Delay.',
  delay: 'How long the reading must remain in breach before the alarm activates. If Repeat is enabled, external email and SMS alerts still have a minimum 15-minute resend interval.',
  repeat: 'Re-sends external email and SMS alerts while the alarm remains active. Alerts cannot resend more often than every 15 minutes, even when Delay is set lower.',
  autoReset: 'Automatically clears the alarm after the sensor reading returns to its permitted range.',
  battery: 'Triggers a low-battery alarm. The voltage threshold is set automatically for the sensor family.',
  missingData: 'Triggers an alarm when the sensor has not reported a reading for the specified number of minutes.',
  disabled: 'Master switch that turns off every alarm for this sensor, regardless of the other settings.',
} as const;

const AlarmSettingHelp = ({ text, align = 'center' }: { text: string; align?: 'left' | 'center' | 'right' }) => (
  <span className="group relative inline-flex normal-case tracking-normal">
    <span
      tabIndex={0}
      role="img"
      aria-label={text}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      className="inline-flex cursor-help rounded-sm text-gray-400 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:hover:text-indigo-400"
    >
      <Info className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
    <span
      role="tooltip"
      className={`pointer-events-none invisible absolute top-full z-50 mt-2 w-64 whitespace-normal rounded-md bg-gray-900 px-3 py-2 text-left text-xs font-normal leading-5 text-white opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 dark:bg-gray-700 ${
        align === 'left' ? 'left-0' : align === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2'
      }`}
    >
      {text}
    </span>
  </span>
);

const defaultAlarm = {
  high_alarm: '',
  low_alarm: '',
  delay: '',
  repeat: false,
  auto_reset: false,
  high_enable: false,
  low_enable: false,
  alarms_disabled: false,
  battery_low_enable: false,
  battery_low_voltage: '',
  missing_data_enable: false,
  missing_data_minutes: '60',
};

export default function AlarmsPage() {
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const { isAdmin } = usePermissions();
  const location = useLocation();

  const { data: sensors, isLoading: sensorsLoading, isError: sensorsError, error: sensorsErrorObj } = useGetSensorListQuery();
  const { data: alarms, isLoading: alarmsLoading, isError: alarmsError, error: alarmsErrorObj, refetch } = useGetAlarmsQuery(undefined, { skip: !user });
  const { data: alarmLogRaw, refetch: refetchAlarmLog } = useGetAlarmLogQuery({ includeAcknowledged: true }, { skip: !user });
  const alarmLog = useMemo(
    () => filterAlarmLogsBySensorAccess(alarmLogRaw, sensors),
    [alarmLogRaw, sensors],
  );
  const [editSensorId, setEditSensorId] = useState<number | null>(null);
  const [editAlarm, setEditAlarm] = useState<any>(null);
  const [updateSensorAlarm, { isLoading: isUpdating }] = useUpdateSensorAlarmMutation();
  const [acknowledgeAlarmLog] = useAcknowledgeAlarmLogMutation();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showBulkToggleDialog, setShowBulkToggleDialog] = useState(false);
  const [isBulkToggling, setIsBulkToggling] = useState(false);
  const [disableReason, setDisableReason] = useState('');
  const [splitDialogSensor, setSplitDialogSensor] = useState<any>(null);
  const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);
  const highlightedRowRef = useRef<HTMLTableRowElement | HTMLDivElement | null>(null);

  // Auto-open edit row when navigated from Sensors page with a highlightSensorId
  useEffect(() => {
    const targetId = (location.state as any)?.highlightSensorId;
    if (!targetId || !sensors || !alarms) return;
    handleEdit(targetId);
    setHighlightedRowId(targetId);
    setTimeout(() => {
      highlightedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightedRowId(null), 2000);
    }, 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, sensors, alarms]);

  // Map alarms by sensor_id for quick lookup
  const alarmMap = (alarms && Array.isArray(alarms)) ? alarms.reduce((acc: any, alarm: any) => {
    acc[alarm.sensor_id] = alarm;
    return acc;
  }, {}) : {};

  const expandedSensors = useMemo(() => expandSensors(sensors ?? []), [sensors]);

  const handleEdit = (sensorId: number) => {
    setEditSensorId(sensorId);
    const base = alarmMap[sensorId] ? { ...alarmMap[sensorId] } : { ...defaultAlarm, sensor_id: sensorId };
    // Normalize the enable flags to "threshold present" since the UI no longer exposes them as separate toggles.
    base.high_enable = base.high_alarm !== '' && base.high_alarm !== null && base.high_alarm !== undefined;
    base.low_enable = base.low_alarm !== '' && base.low_alarm !== null && base.low_alarm !== undefined;
    // Battery low voltage is no longer user-editable — always clear any stored override so the backend
    // uses the per-sensor-family default (TS1 3.4 V, EMS IceSpy 1.4 V).
    base.battery_low_voltage = '';
    setEditAlarm(base);
  };

  const handleCancel = () => {
    setEditSensorId(null);
    setEditAlarm(null);
  };

  const handleSave = async () => {
    if (!editAlarm) return;

    // Reject negative timings before anything else — the block below acknowledges
    // pending alarms, which must not happen for a save we are going to refuse.
    // A negative delay used to make repeat alerts re-fire on every 10s scan.
    const delayVal = editAlarm.delay === '' || editAlarm.delay == null ? null : Number(editAlarm.delay);
    if (delayVal !== null && (!Number.isFinite(delayVal) || delayVal < 0)) {
      setSuccessMessage('Delay must be 0 or more minutes.');
      setTimeout(() => setSuccessMessage(null), 3000);
      return;
    }
    const missingVal = editAlarm.missing_data_minutes === '' || editAlarm.missing_data_minutes == null
      ? null : Number(editAlarm.missing_data_minutes);
    if (missingVal !== null && (!Number.isFinite(missingVal) || missingVal < 0)) {
      setSuccessMessage('Missing data minutes must be 0 or more.');
      setTimeout(() => setSuccessMessage(null), 3000);
      return;
    }

    // Get the original alarm settings for comparison
    const originalAlarm = alarmMap[editAlarm.sensor_id] || { ...defaultAlarm, sensor_id: editAlarm.sensor_id };
    const sensor = sensors?.find((s: any) => s.id === editAlarm.sensor_id);

    // Clear any pending/unacknowledged alarms for this sensor so the new thresholds start fresh
    if (alarmLog && Array.isArray(alarmLog)) {
      const sensorId = typeof editAlarm.sensor_id === 'string' ? parseInt(editAlarm.sensor_id, 10) : editAlarm.sensor_id;
      const pendingAlarms = alarmLog.filter((log: any) => {
        const logSensorId = typeof log.sensor_id === 'string' ? parseInt(log.sensor_id, 10) : log.sensor_id;
        return logSensorId === sensorId && !log.acknowledged && !log.alarm_reset;
      });
      if (pendingAlarms.length > 0) {
        const eventIds = new Set(pendingAlarms.map((log: any) => log.event_id).filter(Boolean));
        const logsToAcknowledge = alarmLog.filter((log: any) => eventIds.has(log.event_id) && !log.acknowledged);
        await Promise.all(
          logsToAcknowledge.map((log: any) =>
            acknowledgeAlarmLog({
              logId: log.idAlarmLog,
              body: {
                acknowledged: true,
                acknowledged_by: 'System',
                alarm_reset: new Date().toISOString(),
                notes: `Auto-cleared on threshold update by ${user?.username || user?.email || 'user'}`,
              }
            }).catch(() => {})
          )
        );
        dispatch(alarmApi.util.invalidateTags(['AlarmLog']));
      }
    }

    await updateSensorAlarm({ sensorId: editAlarm.sensor_id, settings: editAlarm });
    
    // Log threshold update
    const changes = [];
    if (originalAlarm.high_alarm !== editAlarm.high_alarm) {
      changes.push(`High alarm: ${originalAlarm.high_alarm || 'none'} → ${editAlarm.high_alarm || 'none'}`);
    }
    if (originalAlarm.low_alarm !== editAlarm.low_alarm) {
      changes.push(`Low alarm: ${originalAlarm.low_alarm || 'none'} → ${editAlarm.low_alarm || 'none'}`);
    }
    if (originalAlarm.delay !== editAlarm.delay) {
      changes.push(`Delay: ${originalAlarm.delay || 'none'} → ${editAlarm.delay || 'none'}`);
    }
    if (originalAlarm.high_enable !== editAlarm.high_enable) {
      changes.push(`High enabled: ${originalAlarm.high_enable} → ${editAlarm.high_enable}`);
    }
    if (originalAlarm.low_enable !== editAlarm.low_enable) {
      changes.push(`Low enabled: ${originalAlarm.low_enable} → ${editAlarm.low_enable}`);
    }
    if (originalAlarm.battery_low_enable !== editAlarm.battery_low_enable) {
      changes.push(`Battery low enabled: ${originalAlarm.battery_low_enable} → ${editAlarm.battery_low_enable}`);
    }
    if (originalAlarm.missing_data_enable !== editAlarm.missing_data_enable) {
      changes.push(`Missing data enabled: ${originalAlarm.missing_data_enable} → ${editAlarm.missing_data_enable}`);
    }
    if (originalAlarm.missing_data_minutes !== editAlarm.missing_data_minutes) {
      changes.push(`Missing data minutes: ${originalAlarm.missing_data_minutes ?? '60'} → ${editAlarm.missing_data_minutes ?? '60'}`);
    }
    if (originalAlarm.alarms_disabled !== editAlarm.alarms_disabled) {
      changes.push(`Alarms disabled: ${originalAlarm.alarms_disabled} → ${editAlarm.alarms_disabled}`);
    }
    
    await logAuditEvent(
      AUDIT_ACTIONS.SETTINGS_UPDATE,
      `Sensor thresholds updated - Sensor ID: ${editAlarm.sensor_id}, Sensor Name: ${sensor?.name || 'unknown'}, Changes: ${changes.join(', ')}`,
      user // Pass the user data from Redux store
    );
    
    await refetch(); // Ensure the table updates with the latest data
    setEditSensorId(null);
    setEditAlarm(null);
    setSuccessMessage('Alarm settings updated successfully!');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditAlarm((prev: any) => {
      const next = { ...prev, [field]: value };
      // Auto-derive enable flags from the threshold value: presence = on, blank = off.
      // Keeps the simpler UX (no separate High/Low Enable toggles) without disabling alarms.
      if (field === 'high_alarm') {
        next.high_enable = value !== '' && value !== null && value !== undefined;
      } else if (field === 'low_alarm') {
        next.low_enable = value !== '' && value !== null && value !== undefined;
      }
      return next;
    });
  };

  const handleBulkToggleAlarms = async () => {
    if (!sensors || !Array.isArray(sensors)) return;
    if (!isAdmin) {
      setSuccessMessage('Only administrators can disable/enable all alarms.');
      setTimeout(() => setSuccessMessage(null), 3000);
      return;
    }
    
    setIsBulkToggling(true);
    try {
      // Determine if we should disable or enable based on current state
      const enabledSensors = sensors.filter((sensor: any) => {
        const alarm = alarmMap[sensor.id] || { ...defaultAlarm };
        return !alarm.alarms_disabled;
      });
      
      const disabledSensors = sensors.filter((sensor: any) => {
        const alarm = alarmMap[sensor.id] || { ...defaultAlarm };
        return alarm.alarms_disabled;
      });

      // If more sensors are enabled, disable all. Otherwise, enable all.
      const shouldDisable = enabledSensors.length >= disabledSensors.length;
      const sensorsToToggle = shouldDisable ? enabledSensors : disabledSensors;

      // If disabling alarms, acknowledge and clear all active AND pending (pre-alarm) unacknowledged alarms for these sensors
      let acknowledgedAlarmCount = 0;
      if (shouldDisable && alarmLog && Array.isArray(alarmLog)) {
        // Create a set of sensor IDs being disabled for quick lookup (handle both string and number)
        const sensorIdsToDisable = new Set(
          sensorsToToggle.map((s: any) => {
            const id = s.id;
            return typeof id === 'string' ? parseInt(id, 10) : id;
          })
        );
        
        // Find all unacknowledged alarms (both active and pending/pre-alarm) for sensors being disabled
        const allUnacknowledgedAlarms = alarmLog.filter((log: any) => {
          const logSensorId = typeof log.sensor_id === 'string' ? parseInt(log.sensor_id, 10) : log.sensor_id;
          return sensorIdsToDisable.has(logSensorId) && !log.acknowledged && !log.alarm_reset;
        });

        const eventIds = new Set(allUnacknowledgedAlarms.map((log: any) => log.event_id).filter(Boolean));
        const allLogsToAcknowledge = alarmLog.filter((log: any) =>
          eventIds.has(log.event_id) && !log.acknowledged
        );

        acknowledgedAlarmCount = allLogsToAcknowledge.length;

        if (allLogsToAcknowledge.length > 0) {
          const results = await Promise.all(
            allLogsToAcknowledge.map((log: any) =>
              acknowledgeAlarmLog({
                logId: log.idAlarmLog,
                body: {
                  acknowledged: true,
                  acknowledged_by: 'System',
                  alarm_reset: new Date().toISOString(),
                  notes: `Auto-cleared on bulk alarm disable by ${user?.username || user?.email || 'user'}`,
                }
              }).catch((error) => {
                console.error(`Failed to acknowledge alarm log ${log.idAlarmLog}:`, error);
                throw error;
              })
            )
          );
          void results;

          // Invalidate alarm log cache to force refetch
          dispatch(alarmApi.util.invalidateTags(['AlarmLog']));
          
          // Also refetch the alarm log
          await refetchAlarmLog();

          // Log the acknowledgment of alarms
          await logAuditEvent(
            AUDIT_ACTIONS.ALARM_ACKNOWLEDGE,
            `Bulk alarm acknowledgment - Acknowledged ${allLogsToAcknowledge.length} active alarms for ${sensorsToToggle.length} sensors during maintenance/calibration. Sensors: ${sensorsToToggle.map((s: any) => s.name).join(', ')}, Acknowledged by: ${user?.username || 'system'}`,
            user
          );
        }
      }

      // Flip only the master switch. The per-type enables (high/low/battery/missing data,
      // repeat) are left as they are, so re-enabling later restores exactly what was set,
      // even after a page reload. The alarm monitor skips sensors with alarms_disabled.
      await Promise.all(
        sensorsToToggle.map((sensor: any) => {
          const currentAlarm = alarmMap[sensor.id] || { ...defaultAlarm, sensor_id: sensor.id };
          const updatedAlarm = { ...currentAlarm, alarms_disabled: shouldDisable };
          return updateSensorAlarm({ sensorId: sensor.id, settings: updatedAlarm });
        })
      );

      // Log bulk toggle action with reason
      const action = shouldDisable ? 'disable' : 're-enable';
      const reasonText = shouldDisable && disableReason ? ` Reason: ${disableReason}` : '';
      await logAuditEvent(
        AUDIT_ACTIONS.SETTINGS_UPDATE,
        `Bulk alarm ${action} - ${shouldDisable ? 'Disabled' : 'Re-enabled'} alarms for ${sensorsToToggle.length} sensors ${shouldDisable ? 'during' : 'after'} maintenance/calibration.${reasonText} Sensors: ${sensorsToToggle.map((s: any) => s.name).join(', ')}, Performed by: ${user?.username || 'system'}`,
        user
      );

      await refetch();
      setShowBulkToggleDialog(false);
      setDisableReason(''); // Clear the reason after successful operation

      const message = shouldDisable && acknowledgedAlarmCount > 0
        ? `Successfully disabled alarms and cleared ${acknowledgedAlarmCount} active alarm(s) for ${sensorsToToggle.length} sensor(s)!`
        : `Successfully ${shouldDisable ? 'disabled' : 're-enabled'} alarms for ${sensorsToToggle.length} sensor(s)!`;
      
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Bulk toggle error:', error);
      setSuccessMessage('Error toggling alarms. Please try again.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } finally {
      setIsBulkToggling(false);
    }
  };

  // Calculate stats for the cards
  const totalSensors = sensors?.length || 0;
  const enabledAlarms = sensors?.filter((sensor: any) => {
    const alarm = alarmMap[sensor.id] || { ...defaultAlarm };
    return !alarm.alarms_disabled;
  }).length || 0;
  const highAlarmsEnabled = sensors?.filter((sensor: any) => {
    const alarm = alarmMap[sensor.id] || { ...defaultAlarm };
    return alarm.high_enable;
  }).length || 0;
  const lowAlarmsEnabled = sensors?.filter((sensor: any) => {
    const alarm = alarmMap[sensor.id] || { ...defaultAlarm };
    return alarm.low_enable;
  }).length || 0;

  const thresholdStatSummary = useMemo(
    () => ({ totalSensors, enabledAlarms, highAlarmsEnabled, lowAlarmsEnabled }),
    [totalSensors, enabledAlarms, highAlarmsEnabled, lowAlarmsEnabled]
  );

  const activeAlarmSensorIds = useMemo(() => {
    if (!alarmLog || !Array.isArray(alarmLog)) return new Set<number>();
    return new Set(
      alarmLog
        .filter((log: any) => !log.acknowledged && log.active)
        .map((log: any) => typeof log.sensor_id === 'string' ? parseInt(log.sensor_id, 10) : log.sensor_id)
    );
  }, [alarmLog]);

  // Determine toggle button state and text
  const disabledAlarms = sensors?.filter((sensor: any) => {
    const alarm = alarmMap[sensor.id] || { ...defaultAlarm };
    return alarm.alarms_disabled;
  }).length || 0;

  const shouldShowDisable = enabledAlarms >= disabledAlarms;
  const toggleButtonText = shouldShowDisable ? 'Turn Off All Alarms' : 'Re-enable All Alarms';
  const toggleButtonColor = shouldShowDisable ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500';
  const toggleButtonIcon = shouldShowDisable ? X : CheckCircle;

  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 min-w-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8 min-w-0 max-w-full">
        {/* Header Section */}
        <div className="mb-5 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                Threshold Management
              </h1>
              <p className="mt-1 md:mt-2 text-sm md:text-base text-gray-600 dark:text-gray-400">
                Monitor and manage sensor alarm thresholds
              </p>
            </div>
            {isAdmin && (
              <div className="flex items-stretch sm:items-center gap-4 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setShowBulkToggleDialog(true)}
                  className={`inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 sm:py-2 border border-transparent text-sm font-medium rounded-lg text-white ${toggleButtonColor} focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200`}
                  disabled={!sensors || sensors.length === 0}
                >
                  {React.createElement(toggleButtonIcon, { className: "h-4 w-4 mr-2 flex-shrink-0" })}
                  <span className="md:hidden">{shouldShowDisable ? 'Turn off all alarms' : 'Re-enable all alarms'}</span>
                  <span className="hidden md:inline text-left">{toggleButtonText}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <p className="md:hidden mb-4 text-xs text-gray-600 dark:text-gray-400 leading-relaxed tabular-nums">
          <span className="font-medium text-gray-700 dark:text-gray-300">{thresholdStatSummary.totalSensors}</span> sensors
          <span className="mx-1.5 text-gray-400 dark:text-gray-500">·</span>
          <span className="font-medium text-emerald-700 dark:text-emerald-400">{thresholdStatSummary.enabledAlarms}</span> enabled
          <span className="mx-1.5 text-gray-400 dark:text-gray-500">·</span>
          <span className="font-medium text-red-700 dark:text-red-400">{thresholdStatSummary.highAlarmsEnabled}</span> high
          <span className="mx-1.5 text-gray-400 dark:text-gray-500">·</span>
          <span className="font-medium text-amber-700 dark:text-amber-400">{thresholdStatSummary.lowAlarmsEnabled}</span> low
        </p>

        <div className="hidden md:grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border-2 border-indigo-500 dark:border-indigo-400 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center">
                  <Bell className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sensors</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{totalSensors}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border-2 border-indigo-500 dark:border-indigo-400 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Alarms Enabled</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{enabledAlarms}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border-2 border-indigo-500 dark:border-indigo-400 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Alarms</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{highAlarmsEnabled}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border-2 border-indigo-500 dark:border-indigo-400 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Low Alarms</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{lowAlarmsEnabled}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
              <p className="text-sm text-green-700 dark:text-green-400">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Loading and Error States */}
        {(sensorsLoading || alarmsLoading) && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-700 dark:text-gray-200">Loading sensors and alarms...</span>
            </div>
          </div>
        )}

        {(sensorsError || alarmsError) && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <X className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
              <p className="text-sm text-red-700 dark:text-red-400">
                Error loading data: {getErrorMessage(sensorsErrorObj || alarmsErrorObj)}
              </p>
            </div>
          </div>
        )}

        {/* Sensors Table — mobile cards + desktop table */}
        {sensors && Array.isArray(sensors) && (
          <div className="bg-gray-50 dark:bg-gray-800 shadow-lg rounded-xl border border-gray-200 dark:border-gray-700 min-w-0 max-w-full">
            <div className="px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Sensor Thresholds
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Configure alarm settings for each sensor
              </p>
            </div>

            <div className="md:hidden p-4 space-y-3">
              {expandedSensors.map((sensor: SensorRow) => {
                const alarm = alarmMap[sensor.id] || { ...defaultAlarm, sensor_id: sensor.id };
                const isEditing = !isSplitRow(sensor) && editSensorId === sensor.id;
                const isHighlightedCard = sensor.id === highlightedRowId;
                const effHighAlarm = isSplitRow(sensor) ? (sensor._channelConfig?.high_alarm ?? null) : alarm.high_alarm;
                const effLowAlarm = isSplitRow(sensor) ? (sensor._channelConfig?.low_alarm ?? null) : alarm.low_alarm;
                return (
                  <div
                    key={isSplitRow(sensor) ? `${sensor.id}-ch${sensor._channel}` : sensor.id}
                    ref={isHighlightedCard ? (el) => { highlightedRowRef.current = el; } : undefined}
                    className={`rounded-lg border bg-app-panel dark:bg-gray-900/50 p-4 shadow-sm space-y-3 ${isHighlightedCard ? 'border-indigo-400 ring-2 ring-indigo-400' : 'border-gray-200 dark:border-gray-600'}`}
                  >
                    <div className="flex items-start justify-between gap-2 min-w-0">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 dark:bg-indigo-900/20 rounded flex items-center justify-center">
                            <Bell className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate" title={sensor.name}>
                            {sensor.name}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-mono truncate pl-8">{formatSensorSerial(sensor.serialNo, sensor.type)}</p>
                      </div>
                      {!isEditing && isAdmin && (
                        <button
                          type="button"
                          onClick={() => isSplitRow(sensor) ? setSplitDialogSensor(sensor) : handleEdit(sensor.id)}
                          className="flex-shrink-0 inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <Settings className="h-3.5 w-3.5 mr-1" />
                          Edit
                        </button>
                      )}
                    </div>

                    {isEditing ? (
                      <div className="space-y-3 pt-1 border-t border-gray-100 dark:border-gray-700">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              High ({unitLabel(sensor.type)}) <AlarmSettingHelp text={ALARM_SETTING_HELP.high} align="left" />
                            </label>
                            <input
                              type="number"
                              value={editAlarm.high_alarm}
                              onChange={e => handleFieldChange('high_alarm', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              Low ({unitLabel(sensor.type)}) <AlarmSettingHelp text={ALARM_SETTING_HELP.low} align="right" />
                            </label>
                            <input
                              type="number"
                              value={editAlarm.low_alarm}
                              onChange={e => handleFieldChange('low_alarm', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div className="col-span-2 sm:col-span-1">
                            <label className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              Delay (min) <AlarmSettingHelp text={ALARM_SETTING_HELP.delay} align="left" />
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={editAlarm.delay}
                              onChange={e => handleFieldChange('delay', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                          <label className="flex items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-gray-600 px-2 py-2">
                            <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300">
                              Repeat <AlarmSettingHelp text={ALARM_SETTING_HELP.repeat} align="left" />
                            </span>
                            {boolToInput(editAlarm.repeat, v => handleFieldChange('repeat', v))}
                          </label>
                          <label className="flex items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-gray-600 px-2 py-2">
                            <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300">
                              Auto reset <AlarmSettingHelp text={ALARM_SETTING_HELP.autoReset} align="right" />
                            </span>
                            {boolToInput(editAlarm.auto_reset, v => handleFieldChange('auto_reset', v))}
                          </label>
                          {getBatteryLowThresholdVolts(sensor.type) != null && (
                            <label className="flex items-center justify-between gap-2 col-span-2 rounded-md border border-gray-200 dark:border-gray-600 px-2 py-2">
                              <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300">
                                Battery alarm ({getBatteryLowThresholdVolts(sensor.type)} V)
                                <AlarmSettingHelp text={ALARM_SETTING_HELP.battery} align="left" />
                              </span>
                              {boolToInput(!!editAlarm.battery_low_enable, v => handleFieldChange('battery_low_enable', v))}
                            </label>
                          )}
                          <label className="flex flex-col gap-1 col-span-2 rounded-md border border-gray-200 dark:border-gray-600 px-2 py-2">
                            <span className="flex items-center justify-between text-gray-700 dark:text-gray-300">
                              <span className="inline-flex items-center gap-1">
                                Missing data alarm (minutes)
                                <AlarmSettingHelp text={ALARM_SETTING_HELP.missingData} align="left" />
                              </span>
                              {boolToInput(!!editAlarm.missing_data_enable, v => handleFieldChange('missing_data_enable', v))}
                            </span>
                            <input
                              type="number"
                              min={1}
                              value={editAlarm.missing_data_minutes ?? '60'}
                              onChange={e => handleFieldChange('missing_data_minutes', e.target.value)}
                              className="w-full px-2 py-1 text-xs bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md"
                            />
                          </label>
                          <label className="flex items-center justify-between gap-2 col-span-2 rounded-md border border-gray-200 dark:border-gray-600 px-2 py-2">
                            <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300">
                              Alarms disabled <AlarmSettingHelp text={ALARM_SETTING_HELP.disabled} align="left" />
                            </span>
                            {boolToInput(editAlarm.alarms_disabled, v => handleFieldChange('alarms_disabled', v))}
                          </label>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleSave}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-60"
                            disabled={isUpdating}
                          >
                            {isUpdating ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                Save
                              </>
                            ) : (
                              'Save'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancel}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-app-inset dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 block">High</span>
                            <span className="font-medium text-gray-900 dark:text-white tabular-nums">
                              {effHighAlarm != null && effHighAlarm !== '' ? `${effHighAlarm} ${unitLabel(sensor.type)}` : '—'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 block">Low</span>
                            <span className="font-medium text-gray-900 dark:text-white tabular-nums">
                              {effLowAlarm != null && effLowAlarm !== '' ? `${effLowAlarm} ${unitLabel(sensor.type)}` : '—'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 block">Delay</span>
                            <span className="font-medium text-gray-900 dark:text-white tabular-nums">{alarm.delay || '—'}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${alarm.repeat ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`}>
                            Repeat {boolToText(alarm.repeat)}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${alarm.auto_reset ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`}>
                            Auto {boolToText(alarm.auto_reset)}
                          </span>
                          {getBatteryLowThresholdVolts(sensor.type) != null && (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${alarm.battery_low_enable ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/20 dark:text-rose-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`}>
                              Batt {boolToText(!!alarm.battery_low_enable)}
                            </span>
                          )}
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${alarm.missing_data_enable ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/20 dark:text-violet-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`}>
                            Missing Data {boolToText(!!alarm.missing_data_enable)}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${alarm.alarms_disabled ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'}`}>
                            Off {boolToText(alarm.alarms_disabled)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="hidden md:block overflow-x-auto overflow-y-auto max-h-[calc(100vh-420px)] min-h-64">
              {/* overflow-y-auto + max-h makes this div the scroll container so sticky top-0 on <th> works */}
              <table className="w-full min-w-0 border-separate border-spacing-0">
                <thead>
                    <tr>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-64 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">Sensor Name</th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">Serial No</th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <span className="inline-flex items-center gap-1">High <AlarmSettingHelp text={ALARM_SETTING_HELP.high} /></span>
                      </th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <span className="inline-flex items-center gap-1">Low <AlarmSettingHelp text={ALARM_SETTING_HELP.low} /></span>
                      </th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-14 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <span className="inline-flex items-center gap-1">Delay <AlarmSettingHelp text={ALARM_SETTING_HELP.delay} /></span>
                      </th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-14 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <span className="inline-flex items-center gap-1">Repeat <AlarmSettingHelp text={ALARM_SETTING_HELP.repeat} /></span>
                      </th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <span className="inline-flex items-center gap-1">Auto Reset <AlarmSettingHelp text={ALARM_SETTING_HELP.autoReset} /></span>
                      </th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <span className="inline-flex items-center gap-1">Batt ↓ <AlarmSettingHelp text={ALARM_SETTING_HELP.battery} align="right" /></span>
                      </th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <span className="inline-flex items-center gap-1">Missing Data <AlarmSettingHelp text={ALARM_SETTING_HELP.missingData} align="right" /></span>
                      </th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <span className="inline-flex items-center gap-1">Disabled <AlarmSettingHelp text={ALARM_SETTING_HELP.disabled} align="right" /></span>
                      </th>
                      <th className="sticky top-0 z-20 px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16 whitespace-nowrap bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-50 dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {expandedSensors.map((sensor: SensorRow) => {
                      const alarm = alarmMap[sensor.id] || { ...defaultAlarm, sensor_id: sensor.id };
                      const isEditing = !isSplitRow(sensor) && editSensorId === sensor.id;
                      const hasActiveAlarm = activeAlarmSensorIds.has(sensor.id);
                      const isHighlighted = sensor.id === highlightedRowId;
                      const effHighAlarm = isSplitRow(sensor) ? (sensor._channelConfig?.high_alarm ?? null) : alarm.high_alarm;
                      const effLowAlarm = isSplitRow(sensor) ? (sensor._channelConfig?.low_alarm ?? null) : alarm.low_alarm;
                      return (
                        <tr
                          key={isSplitRow(sensor) ? `${sensor.id}-ch${sensor._channel}` : sensor.id}
                          ref={isHighlighted ? (el) => { highlightedRowRef.current = el; } : undefined}
                          className={`transition-colors duration-150 ${isHighlighted ? 'ring-2 ring-inset ring-indigo-400' : ''} ${hasActiveAlarm ? 'bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 dark:bg-indigo-900/20 rounded flex items-center justify-center">
                                <Bell className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
                              </div>
                              <div className="ml-2">
                                <div 
                                  className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-56"
                                  title={sensor.name}
                                >
                                  {sensor.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white font-mono">{formatSensorSerial(sensor.serialNo, sensor.type)}</td>
                          {isEditing ? (
                            <>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={editAlarm.high_alarm}
                                    onChange={e => handleFieldChange('high_alarm', e.target.value)}
                                    title={`High alarm threshold (${unitLabel(sensor.type)})`}
                                    className="w-16 px-2 py-1 text-xs bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                  />
                                  <span className="text-xs text-gray-400">{unitLabel(sensor.type)}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={editAlarm.low_alarm}
                                    onChange={e => handleFieldChange('low_alarm', e.target.value)}
                                    title={`Low alarm threshold (${unitLabel(sensor.type)})`}
                                    className="w-16 px-2 py-1 text-xs bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                  />
                                  <span className="text-xs text-gray-400">{unitLabel(sensor.type)}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={editAlarm.delay}
                                  onChange={e => handleFieldChange('delay', e.target.value)}
                                  title="Delay before alarm activates (minutes). Also sets how often repeat alerts re-send, with a 15 minute minimum."
                                  className="w-20 px-2 py-1 text-xs bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap"><div title="Repeat: re-send alert every Delay minutes while still in breach (minimum 15 minutes)">{boolToInput(editAlarm.repeat, v => handleFieldChange('repeat', v))}</div></td>
                              <td className="px-4 py-3 whitespace-nowrap"><div title="Auto Reset: automatically clear the alarm when temperature returns to normal">{boolToInput(editAlarm.auto_reset, v => handleFieldChange('auto_reset', v))}</div></td>
                              <td className="px-4 py-3 whitespace-nowrap align-top">
                                {getBatteryLowThresholdVolts(sensor.type) != null ? (
                                  <div title={`Trigger when battery drops below ${getBatteryLowThresholdVolts(sensor.type)} V (auto-set for this sensor family).`}>
                                    {boolToInput(!!editAlarm.battery_low_enable, v => handleFieldChange('battery_low_enable', v))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap align-top">
                                <div className="flex flex-col gap-1 max-w-[4.5rem]" title="Alert if latest reading is older than this (minutes).">
                                  {boolToInput(!!editAlarm.missing_data_enable, v => handleFieldChange('missing_data_enable', v))}
                                  <input
                                    type="number"
                                    min={1}
                                    value={editAlarm.missing_data_minutes ?? '60'}
                                    onChange={e => handleFieldChange('missing_data_minutes', e.target.value)}
                                    className="w-full px-1 py-0.5 text-xs bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded"
                                  />
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap"><div title="Disabled: master switch — turns off all alarms for this sensor regardless of other settings">{boolToInput(editAlarm.alarms_disabled, v => handleFieldChange('alarms_disabled', v))}</div></td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end space-x-1">
                                  <button 
                                    onClick={handleSave} 
                                    className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors duration-200"
                                    disabled={isUpdating}
                                  >
                                    {isUpdating ? (
                                      <>
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                        Save
                                      </>
                                    ) : (
                                      'Save'
                                    )}
                                  </button>
                                  <button 
                                    onClick={handleCancel} 
                                    className="inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-app-inset dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {effHighAlarm != null && effHighAlarm !== '' ? `${effHighAlarm} ${unitLabel(sensor.type)}` : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {effLowAlarm != null && effLowAlarm !== '' ? `${effLowAlarm} ${unitLabel(sensor.type)}` : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">{alarm.delay || '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${alarm.repeat ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`}>
                                  {boolToText(alarm.repeat)}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${alarm.auto_reset ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`}>
                                  {boolToText(alarm.auto_reset)}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-white">
                                {getBatteryLowThresholdVolts(sensor.type) == null ? (
                                  '—'
                                ) : alarm.battery_low_enable ? (
                                  <span title={`Trigger when battery drops below ${getBatteryLowThresholdVolts(sensor.type)} V`}>
                                    On ({getBatteryLowThresholdVolts(sensor.type)} V)
                                  </span>
                                ) : (
                                  <span className="text-gray-500">Off</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-white">
                                {alarm.missing_data_enable ? (
                                  <span>&gt; {alarm.missing_data_minutes ?? 60} min</span>
                                ) : (
                                  <span className="text-gray-500">Off</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${alarm.alarms_disabled ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'}`}>
                                  {boolToText(alarm.alarms_disabled)}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                {isAdmin && (
                                  <button
                                    onClick={() => isSplitRow(sensor) ? setSplitDialogSensor(sensor) : handleEdit(sensor.id)}
                                    className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                                  >
                                    <Settings className="h-3 w-3 mr-1" />
                                    Edit
                                  </button>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {splitDialogSensor && (
          <SplitChannelDialog sensor={splitDialogSensor} onClose={() => setSplitDialogSensor(null)} />
        )}

        {/* Bulk Toggle Confirmation Dialog */}
        {showBulkToggleDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 w-full max-w-md mx-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{toggleButtonText}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {shouldShowDisable 
                    ? 'This will disable High EN and Low EN for all sensors and automatically acknowledge/clear any active pending alarms. This is typically used during calibration or site maintenance.'
                    : 'This will re-enable alarms for all sensors that were previously disabled, restoring their original High EN and Low EN settings. Use this after maintenance or calibration is complete.'
                  }
                </p>
                {shouldShowDisable && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reason for disabling alarms <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={disableReason}
                      onChange={(e) => setDisableReason(e.target.value)}
                      placeholder="e.g., Site maintenance, sensor calibration, system testing..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                      rows={3}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      A reason is required for audit purposes.
                    </p>
                  </div>
                )}
                <div className={`mt-3 p-3 border rounded-lg ${
                  shouldShowDisable 
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' 
                    : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                }`}>
                  <p className={`text-sm ${
                    shouldShowDisable 
                      ? 'text-amber-700 dark:text-amber-400' 
                      : 'text-emerald-700 dark:text-emerald-400'
                  }`}>
                    <strong>{shouldShowDisable ? 'Warning:' : 'Note:'}</strong> {shouldShowDisable 
                      ? 'This action will disable High EN and Low EN for all sensors and clear any active pending alarms. Original settings will be saved and restored when you re-enable alarms. Make sure to re-enable them after maintenance is complete.'
                      : 'This will restore alarm functionality for all sensors that were disabled during maintenance, including their original High EN and Low EN settings.'
                    }
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-app-inset dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                  onClick={() => {
                    setShowBulkToggleDialog(false);
                    setDisableReason('');
                  }}
                  disabled={isBulkToggling}
                >
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white ${toggleButtonColor} focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-60`}
                  onClick={handleBulkToggleAlarms}
                  disabled={isBulkToggling || (shouldShowDisable && !disableReason.trim())}
                >
                  {isBulkToggling ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      {shouldShowDisable ? 'Disabling...' : 'Re-enabling...'}
                    </>
                  ) : (
                    toggleButtonText
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
